// Typed errors. The client branches on ErrorCode only; the server's message goes to
// the log, never into a condition in code. Mirrors the `Error.code` enum in
// api-spec/openapi/common.yaml plus the conditions recognised on the client side.

export type ErrorCode =
  // Server responses.
  | 'bad_request'
  | 'unauthenticated'
  | 'invalid_signature'
  | 'session_expired'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'link_not_allowed'
  | 'content_rejected'
  | 'rate_limited'
  | 'number_not_allowed'
  | 'invalid_code'
  | 'code_expired'
  | 'device_quarantined'
  | 'unsupported_version'
  | 'entry_withdrawn'
  | 'media_too_large'
  | 'media_unsupported'
  | 'server_error'
  | 'service_unavailable'
  // Conditions recognised on the client side.
  | 'no_network'
  | 'unreadable_response'
  | 'manifest_bad_signature'
  | 'manifest_too_old'
  | 'manifest_expired'
  | 'clock_unreliable'
  | 'directory_not_configured'
  | 'device_key_unavailable'
  /** Retired with the bulk snapshot; kept so old diagnostics stay readable. */
  | 'snapshot_digest_mismatch'
  /** A point lookup came back without a valid inclusion proof. */
  | 'entry_proof_invalid'
  | 'unknown';

const KNOWN_SERVER_CODES: ReadonlySet<string> = new Set<string>([
  'bad_request',
  'unauthenticated',
  'invalid_signature',
  'session_expired',
  'forbidden',
  'not_found',
  'conflict',
  'link_not_allowed',
  'content_rejected',
  'rate_limited',
  'number_not_allowed',
  'invalid_code',
  'code_expired',
  'device_quarantined',
  'unsupported_version',
  'entry_withdrawn',
  'media_too_large',
  'media_unsupported',
  'server_error',
  'service_unavailable',
]);

export function errorCodeFromName(name: unknown): ErrorCode {
  return typeof name === 'string' && KNOWN_SERVER_CODES.has(name) ? (name as ErrorCode) : 'unknown';
}

/** The HTTP-status fallback when the body carries no `code` — same table as the
 *  reference client. */
export function errorCodeFromStatus(status: number): ErrorCode {
  switch (status) {
    case 400:
      return 'bad_request';
    case 401:
      return 'session_expired';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 413:
      return 'media_too_large';
    case 415:
      return 'media_unsupported';
    case 422:
      return 'content_rejected';
    case 423:
      return 'entry_withdrawn';
    case 429:
      return 'rate_limited';
    case 503:
      return 'service_unavailable';
    default:
      return 'server_error';
  }
}

export class ClientError extends Error {
  readonly code: ErrorCode;
  /** Seconds, from the body's `retry_after_s` — the server's own deadline. */
  readonly retryAfterS: number | undefined;

  constructor(code: ErrorCode, options?: { message?: string; retryAfterS?: number }) {
    super(options?.message ?? code);
    this.name = 'ClientError';
    this.code = code;
    this.retryAfterS = options?.retryAfterS;
  }

  /** Whether repeating the same request could ever succeed. The outbox uses this to
   *  decide between keeping an item queued and marking it permanently failed. */
  get retryable(): boolean {
    return (
      this.code === 'no_network' ||
      this.code === 'service_unavailable' ||
      this.code === 'server_error' ||
      this.code === 'rate_limited'
    );
  }

  /** Whether the number has to be verified again with this company. */
  get needsLogin(): boolean {
    return this.code === 'session_expired' || this.code === 'unauthenticated';
  }
}

export function asClientError(error: unknown): ClientError {
  return error instanceof ClientError
    ? error
    : new ClientError('unknown', { message: error instanceof Error ? error.message : String(error) });
}
